/**
 * MarkFundedButton — admin action that flips a signed landlord quote to
 * "funded". Prevents crews rolling out on unfunded work. Small popover form:
 * pick method (AppFolio / wire / check / other), optional reference #, optional
 * note. Fires POST /api/jobs/:id/landlord-quote/mark-funded.
 */
import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Bank } from "@phosphor-icons/react";

export default function MarkFundedButton({ jobId, onDone }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("appfolio");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const mark = async () => {
    setBusy(true);
    const t = toast.loading("Marking as funded…");
    try {
      await api.post(`/jobs/${jobId}/landlord-quote/mark-funded`, {
        funded: true, method, reference, note,
      });
      toast.success("Quote marked funded — safe to dispatch", { id: t });
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not mark funded", { id: t });
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} data-testid="mark-funded-btn"
        className="tactile-btn h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-wider text-xs inline-flex items-center gap-1.5 shadow-[3px_3px_0_#0f172a]">
        <Bank weight="fill" size={14}/> Mark Funded
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
         onClick={() => setOpen(false)}>
      <div className="bg-white w-full max-w-md border-4 border-emerald-600 shadow-[8px_8px_0_#0f172a]"
           onClick={(e) => e.stopPropagation()}
           data-testid="mark-funded-modal">
        <div className="bg-emerald-600 text-white p-4">
          <div className="text-[10px] font-black uppercase tracking-[.2em] opacity-70">Funding confirmation</div>
          <h2 className="text-xl font-black mt-1">Mark Quote as Funded</h2>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-slate-600">Funding Method</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)}
                    data-testid="funded-method"
                    className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] bg-white font-bold">
              <option value="appfolio">AppFolio Disbursement</option>
              <option value="wire">Wire Transfer</option>
              <option value="check">Check</option>
              <option value="cash">Cash</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-slate-600">Reference #</label>
            <input value={reference} onChange={(e) => setReference(e.target.value)}
                   placeholder="e.g., AppFolio disbursement ID or check number"
                   data-testid="funded-reference"
                   className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] font-medium"/>
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-widest text-slate-600">Note (optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)}
                      rows={2} placeholder="Anything else worth remembering"
                      data-testid="funded-note"
                      className="mt-1 w-full p-3 border-2 border-[#0f172a] text-sm font-medium"/>
          </div>
        </div>
        <div className="p-4 border-t-2 border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={() => setOpen(false)}
                  className="h-10 px-4 border-2 border-[#0f172a] bg-white font-black uppercase text-xs tracking-widest">
            Cancel
          </button>
          <button onClick={mark} disabled={busy}
                  data-testid="mark-funded-confirm"
                  className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-xs tracking-widest
                             inline-flex items-center gap-2 disabled:opacity-60 shadow-[3px_3px_0_#0f172a]">
            <CheckCircle weight="fill" size={14}/>
            {busy ? "Saving…" : "Confirm Funded"}
          </button>
        </div>
      </div>
    </div>
  );
}
